/**
 * Fonte única dos nomes de evento de socket — mesma regra do `HOOK_EVENTS`
 * do @markedesk/plugin-sdk ("o nome do evento existe em UM lugar"): nunca
 * escreva a string na mão, importe daqui. Agrupado por domínio, igual
 * `events/domains/` do plugin-sdk.
 *
 * Sem companyId embutido — o namespace do socket (`io.of(String(companyId))`)
 * já isola por empresa; ver [[project_core_contracts_pacote]].
 *
 * Cresce como o resto do pacote — só entra aqui o que for corrigido/tocado,
 * não é objetivo migrar os ~28 nomes de uma vez (ver CLAUDE.md).
 */
export const SOCKET_EVENTS = {
  QUEUE: {
    /** Fila criada/editada/excluída — cachedApi.queues (frontend). */
    LIST: "queue",
  },
  TAG: {
    /** Tag criada/editada/excluída/sincronizada — cachedApi.tags. */
    LIST: "tag",
  },
  AUDIT: {
    /**
     * Registro novo de auditoria — só para a sala AUDIT_SOCKET.ROOM (quem está
     * com a tela de auditoria aberta), nunca para a empresa inteira.
     */
    RECORDED: "auditRecorded",
  },
  COMPANY_SETTINGS: {
    /** Uma configuração da empresa mudou — cachedApi.companySettings. */
    ONE: "companySettings",
  },
  PLAN: {
    /** Plano da empresa trocado pelo super admin — cachedApi.plans. */
    UPDATED: "plan",
  },
  USER: {
    /**
     * Aviso dirigido a uma pessoa: vai só para a room de cada destinatário
     * (domains/user: userSocket.notify), nunca para a empresa. O `kind` do
     * payload diz o que é — o primeiro foi o lembrete de tarefa (#4443).
     */
    NOTIFICATION: "userNotification",
  },
  TICKET: {
    /** Job de transferência em massa (TicketController) — pages/Connections. */
    TRANSFER: "transferTickets",
    /**
     * Ticket criado/mudou de status/dono/fila — TicketsListCustom, Ticket,
     * NotificationsPopOver, Kanban, MomentsUser (frontend). `action: "delete"`
     * tira da tela de quem não deve mais ver; `action: "update"` entrega a
     * quem deve. Até 24-09-2026 o nome embutia `company-${companyId}-`,
     * redundante com o namespace do socket que já isola por empresa (#4421) —
     * sem companyId igual aos demais eventos deste arquivo.
     */
    LIST: "ticket",
  },
  CONTACT: {
    /** Contato criado/editado/excluído (inclusive em massa e no import). */
    CHANGED: "contact",
    /** Mesclagem de contatos terminou (job em fila, #4408). */
    MERGE: "contactMerge",
  },
  CHAT: {
    /**
     * Lista de chats: mensagem nova, chat lido, chat apagado. Vai só para a
     * room de cada participante (backend: emitToChatMembers), nunca para a
     * empresa inteira — o payload leva o texto da mensagem.
     */
    LIST: "chat",
    /** Chat criado ou editado, entregue a cada participante na room dele. */
    USER: "chatUser",
    /** Conversa aberta: mensagem nova, editada, apagada, chat lido. */
    CONVERSATION: (chatId: number | string) => `chat-${chatId}` as const,
  },
  PRESENCE: {
    /**
     * Um atendente ficou online. Vai para a empresa inteira (todo mundo vê o
     * indicador dos colegas).
     *
     * Os nomes carregam ":" por herança do código antigo, onde eram string
     * digitada nos dois lados (#4433). Mantidos como estavam para não exigir
     * front e back no ar ao mesmo tempo no deploy.
     */
    USER_ONLINE: "user:online",
    /** Um atendente ficou offline. */
    USER_OFFLINE: "user:offline",
    /** Lista de quem já está online, enviada só a quem acabou de conectar. */
    ONLINE_LIST: "users:online",
    /** Um atendente entrou na empresa (apareceu para os colegas). */
    USER_NEW: "user:new",
  },
  WHATSAPP: {
    /** Conexão criada/editada/removida. */
    CHANGED: "whatsapp",
    /** Estado da sessão: status, qrcode, progresso de conexão. */
    SESSION: "whatsappSession",
  },
} as const;
